import React, { useState, useEffect } from 'react';
import historyService, { HistoryPesee, HistoryFilters } from '@/services/historyService';
import productsService from '@/services/productsService';
// import exportService from '@/services/exportService'; // Non utilisé
import HistoryActionsMenu from '@/components/HistoryActionsMenu';
import LoadingSpinner from '@/components/LoadingSpinner';
import EmailModal from '@/components/tickets/EmailModal';
import AdvancedSearchModal from '@/components/AdvancedSearchModal';
import SearchableSelect from '@/components/SearchableSelect';
import ExportModal from '@/components/export/ExportModal';
import PermissionButton from '@/components/permissions/PermissionButton';
import PeseeDetailsModal from '@/components/PeseeDetailsModal';
import { RefoulementModal } from '@/components/modals';
import { PERMISSIONS } from '@/types/permissions';
import refoulementTicketService from '@/services/refoulementTicketService';
import { convertToRefoulementTicketData } from '@/utils/refoulementTicketConverter';
import weighingService from '@/services/weighing';
import type { Pesée } from '@/types/weighing';
// import { convertTicketToWeighingTicketData } from '@/utils/ticketConversion'; // Non utilisé

const HistoryPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'list' | 'bordereau'>('list');
  const [weighings, setWeighings] = useState<HistoryPesee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [filters, setFilters] = useState<HistoryFilters>({
    search: '',
    produit: '',
    statut: '',
    type_pesee: '',
    date_from: '',
    date_to: '',
    vehicule: '',
    type_vehicule: '',
    remorque: '',
    chauffeur: '',
    convoyeur: '',
    client: '',
    fournisseur: '',
    transporteur: '',
    exportateur: '',
    magasin: '',
    conditionnement: '',
    provenance: '',
    page: 1,
    page_size: 20
  });
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // États pour les actions
  const [selectedPesee, setSelectedPesee] = useState<HistoryPesee | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [productOptions, setProductOptions] = useState<{value: string, label: string}[]>([]);
  // const [exporting, setExporting] = useState(false); // Non utilisé
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState<'pdf' | 'excel' | 'csv'>('pdf');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRefoulementModal, setShowRefoulementModal] = useState(false);
  const [selectedPeseeForRefoulement, setSelectedPeseeForRefoulement] = useState<HistoryPesee | null>(null);
  const [emailData, setEmailData] = useState({
    recipients: '',
    subject: '',
    message: '',
    cc: '',
    bcc: ''
  });

  // États pour le bordereau
  const [bordereauFilters, setBordereauFilters] = useState({
    dateFrom: '',
    dateTo: '',
    produit: '',
    fournisseur: '',
    provenance: '',
    statut: '', // Tous les statuts pour alimenter les cartes récap
    type_pesee: '',
    vehicule: '',
    type_vehicule: '',
    remorque: '',
    chauffeur: '',
    convoyeur: '',
    client: '',
    transporteur: '',
    exportateur: '',
    magasin: '',
    conditionnement: '',
  });
  const [bordereauWeighings, setBordereauWeighings] = useState<HistoryPesee[]>([]);
  const [bordereauLoading, setBordereauLoading] = useState(false);
  const [showBordereauPreview, setShowBordereauPreview] = useState(false);
  const [showBordereauAdvancedSearch, setShowBordereauAdvancedSearch] = useState(false);

  useEffect(() => {
    loadWeighings();
  }, [filters]);

  useEffect(() => {
    loadProductOptions();
  }, []);

  const loadWeighings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await historyService.getPesees(filters);
      if (response.success) {
        setWeighings(response.data);
        setTotalCount(response.count || response.data.length);
        setTotalPages(Math.ceil((response.count || response.data.length) / (filters.page_size || 20)));
      } else {
        setError(response.message);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des pesées:', error);
      setError(error.message || 'Erreur lors du chargement des pesées');
    } finally {
      setLoading(false);
    }
  };

  const loadProductOptions = async () => {
    try {
      const options = await productsService.getProducts();
      setProductOptions(options);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    }
  };

  // Fonctions pour le bordereau
  const loadBordereauWeighings = async () => {
    try {
      setBordereauLoading(true);
      const filters: HistoryFilters = {
        search: '',
        produit: bordereauFilters.produit,
        statut: bordereauFilters.statut,
        date_from: bordereauFilters.dateFrom,
        date_to: bordereauFilters.dateTo,
        fournisseur: bordereauFilters.fournisseur,
        provenance: bordereauFilters.provenance,
        type_pesee: bordereauFilters.type_pesee,
        vehicule: bordereauFilters.vehicule,
        type_vehicule: bordereauFilters.type_vehicule,
        remorque: bordereauFilters.remorque,
        chauffeur: bordereauFilters.chauffeur,
        convoyeur: bordereauFilters.convoyeur,
        client: bordereauFilters.client,
        transporteur: bordereauFilters.transporteur,
        exportateur: bordereauFilters.exportateur,
        magasin: bordereauFilters.magasin,
        conditionnement: bordereauFilters.conditionnement,
        page: 1,
        page_size: 1000 // Charger toutes les pesées pour le bordereau
      };
      
      const response = await historyService.getPesees(filters);
      if (response.success) {
        setBordereauWeighings(response.data);
      } else {
        setError(response.message);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des pesées pour le bordereau:', error);
      setError(error.message || 'Erreur lors du chargement des pesées');
    } finally {
      setBordereauLoading(false);
    }
  };

  const handleBordereauFilterChange = (field: string, value: string) => {
    setBordereauFilters(prev => ({ ...prev, [field]: value }));
  };

  const getBordereauAdvancedFiltersCount = () => {
    const keys = [
      'vehicule', 'type_vehicule', 'remorque', 'chauffeur', 'convoyeur',
      'client', 'transporteur', 'exportateur', 'magasin', 'conditionnement',
    ] as const;
    return keys.filter((key) => Boolean(bordereauFilters[key])).length;
  };

  const handleBordereauAdvancedSearch = async (newFilters: HistoryFilters) => {
    const nextFilters = {
      dateFrom: newFilters.date_from || bordereauFilters.dateFrom,
      dateTo: newFilters.date_to || bordereauFilters.dateTo,
      produit: newFilters.produit ?? bordereauFilters.produit,
      statut: newFilters.statut || bordereauFilters.statut,
      type_pesee: newFilters.type_pesee ?? bordereauFilters.type_pesee,
      fournisseur: newFilters.fournisseur ?? bordereauFilters.fournisseur,
      provenance: newFilters.provenance ?? bordereauFilters.provenance,
      vehicule: newFilters.vehicule || '',
      type_vehicule: newFilters.type_vehicule || '',
      remorque: newFilters.remorque || '',
      chauffeur: newFilters.chauffeur || '',
      convoyeur: newFilters.convoyeur || '',
      client: newFilters.client || '',
      transporteur: newFilters.transporteur || '',
      exportateur: newFilters.exportateur || '',
      magasin: newFilters.magasin || '',
      conditionnement: newFilters.conditionnement || '',
    };
    setBordereauFilters(nextFilters);
    setShowBordereauAdvancedSearch(false);

    try {
      setBordereauLoading(true);
      const response = await historyService.getPesees({
        produit: nextFilters.produit,
        statut: nextFilters.statut,
        date_from: nextFilters.dateFrom,
        date_to: nextFilters.dateTo,
        fournisseur: nextFilters.fournisseur,
        provenance: nextFilters.provenance,
        type_pesee: nextFilters.type_pesee,
        vehicule: nextFilters.vehicule,
        type_vehicule: nextFilters.type_vehicule,
        remorque: nextFilters.remorque,
        chauffeur: nextFilters.chauffeur,
        convoyeur: nextFilters.convoyeur,
        client: nextFilters.client,
        transporteur: nextFilters.transporteur,
        exportateur: nextFilters.exportateur,
        magasin: nextFilters.magasin,
        conditionnement: nextFilters.conditionnement,
        page: 1,
        page_size: 1000,
      });
      if (response.success) {
        setBordereauWeighings(response.data);
      } else {
        setError(response.message);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des pesées pour le bordereau:', error);
      setError(error.message || 'Erreur lors du chargement des pesées');
    } finally {
      setBordereauLoading(false);
    }
  };

  const clearBordereauAdvancedFilters = () => {
    setBordereauFilters(prev => ({
      ...prev,
      vehicule: '',
      type_vehicule: '',
      remorque: '',
      chauffeur: '',
      convoyeur: '',
      client: '',
      transporteur: '',
      exportateur: '',
      magasin: '',
      conditionnement: '',
    }));
  };

  const generateBordereau = () => {
    if (bordereauWeighings.length === 0) {
      alert('Aucune pesée trouvée pour générer le bordereau');
      return;
    }
    setShowBordereauPreview(true);
  };

  const printBordereau = () => {
    const bordereauHTML = generateBordereauHTML();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(bordereauHTML);
      printWindow.document.close();
      
      // Attendre que les images se chargent avant d'imprimer
      printWindow.onload = () => {
        setTimeout(() => {
      printWindow.print();
        }, 1000);
      };
    }
  };

  const generateBordereauHTML = (): string => {
    const totalNSA = bordereauWeighings.length;
    const totalPNAcc = bordereauWeighings.reduce((sum, weighing) => {
      const poidsNet = weighing.poids_net;
      if (typeof poidsNet === 'string') {
        // Parser les chaînes de caractères pour extraire les nombres
        const cleanString = String(poidsNet).replace(/[^\d.,]/g, '').replace(',', '.');
        const parsed = parseFloat(cleanString);
        return sum + (isNaN(parsed) ? 0 : parsed);
      } else if (typeof poidsNet === 'number') {
        return sum + poidsNet;
      }
      return sum;
    }, 0);
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Fiche Récapitulative</title>
        <style>
          @page { 
            size: A4 landscape; 
            margin: 1cm; 
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 20px; 
            font-size: 12px;
          }
          .header { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            margin-bottom: 20px; 
          }
          .logo-section { 
            text-align: left; 
            flex: 1;
          }
          .logo-section img { 
            max-height: 80px; 
            max-width: 120px;
            display: block;
          }
          .title-section { 
            text-align: center; 
            flex: 2;
          }
          .title-section h1 { 
            font-size: 24px; 
            font-weight: bold; 
            margin: 0; 
          }
          .document-number { 
            font-size: 18px; 
            font-weight: bold; 
            margin: 10px 0; 
          }
          .right-logo { 
            text-align: right; 
            flex: 1;
          }
          .right-logo img { 
            max-height: 80px; 
            max-width: 120px;
            display: block;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 20px; 
          }
          th, td { 
            border: 1px solid #000; 
            padding: 6px; 
            text-align: left; 
            font-size: 11px; 
          }
          .center { 
            text-align: center; 
          }
          th { 
            background-color: #f0f0f0; 
            font-weight: bold; 
          }
          .total-row { 
            font-weight: bold; 
            background-color: #e0e0e0; 
          }
          .signature-section { 
            display: flex; 
            justify-content: space-between; 
            margin-top: 40px; 
          }
          .signature-box { 
            width: 200px; 
            text-align: center; 
          }
          .signature-line { 
            border-bottom: 1px solid #000; 
            height: 40px; 
            margin-bottom: 5px; 
          }
          .date { 
            text-align: right; 
            margin-bottom: 20px; 
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo-section">
            <img src="${window.location.origin}/images/cca-logo.svg" alt="CCA Logo" style="max-height: 80px; max-width: 120px;" />
          </div>
          <div class="title-section">
            <h1>FICHE RECAPITULATIVE</h1>
            <div class="document-number">30 711 005</div>
          </div>
          <div class="right-logo">
            <img src="${window.location.origin}/images/logo-pontcita.png" alt="Pont CITA Logo" style="max-height: 80px; max-width: 120px;" />
          </div>
        </div>
        
        <div class="date">
          Date: ${new Date().toLocaleDateString('fr-FR')}
        </div>
        
        <!-- Critères de génération -->
        ${(() => {
          const criteres = [];
          if (bordereauFilters.dateFrom && bordereauFilters.dateTo) {
            criteres.push(`Période: ${bordereauFilters.dateFrom} au ${bordereauFilters.dateTo}`);
          }
          if (bordereauFilters.produit) {
            criteres.push(`Produit: ${bordereauFilters.produit}`);
          }
          if (bordereauFilters.fournisseur) {
            criteres.push(`Fournisseur: ${bordereauFilters.fournisseur}`);
          }
          if (bordereauFilters.provenance) {
            criteres.push(`Provenance: ${bordereauFilters.provenance}`);
          }
          if (bordereauFilters.type_pesee) {
            criteres.push(`Type: ${bordereauFilters.type_pesee}`);
          }
          if (bordereauFilters.statut) {
            criteres.push(`Statut: ${bordereauFilters.statut}`);
          }
          if (bordereauFilters.type_vehicule) {
            criteres.push(`Type véhicule: ${bordereauFilters.type_vehicule}`);
          }
          if (bordereauFilters.client) {
            criteres.push(`Client: #${bordereauFilters.client}`);
          }
          if (bordereauFilters.magasin) {
            criteres.push(`Magasin: #${bordereauFilters.magasin}`);
          }
          if (bordereauFilters.conditionnement) {
            criteres.push(`Conditionnement: #${bordereauFilters.conditionnement}`);
          }
          
          if (criteres.length > 0) {
            return `
              <div class="criteria-section">
                <h3 style="font-size: 14px; font-weight: bold; margin: 10px 0 5px 0; color: #333;">Critères de génération:</h3>
                <div style="font-size: 11px; color: #666; margin-bottom: 15px;">
                  ${criteres.join(' | ')}
                </div>
              </div>
            `;
          }
          return '';
        })()}
        
        <table>
          <thead>
            <tr>
              <th>N°Pesee / N°Ticket</th>
              <th>Camion</th>
              <th>Date</th>
              <th>cnsment</th>
              <th>Provenance</th>
              <th>Fournisseur</th>
              <th class="center">NSA</th>
              <th class="center">PNAcc</th>
              <th>Produit</th>
              <th>N°Lot</th>
            </tr>
          </thead>
          <tbody>
            ${bordereauWeighings.map((weighing) => {
              // Parser le poids net pour l'affichage
              const poidsNet = weighing.poids_net;
              let poidsNetParsed = 0;
              if (typeof poidsNet === 'string') {
                const cleanString = String(poidsNet).replace(/[^\d.,]/g, '').replace(',', '.');
                poidsNetParsed = parseFloat(cleanString);
                if (isNaN(poidsNetParsed)) poidsNetParsed = 0;
              } else if (typeof poidsNet === 'number') {
                poidsNetParsed = poidsNet;
              }
              
              // Construire l'affichage du camion avec marque et immatriculation
              const camionInfo = weighing.camion_immatriculation || 'FOURCHET';
              // @ts-ignore - Propriété camion_marque peut exister dans les données
              const marqueCamion = weighing.camion_marque || '';
              const camionDisplay = marqueCamion ? `${marqueCamion} - ${camionInfo}` : camionInfo;
              
              return `
              <tr>
                <td>${weighing.reference || weighing.id}</td>
                  <td>${camionDisplay}</td>
                <td>${formatDate(weighing.date_heure)}</td>
                <td></td>
                <td>${weighing.provenance || 'MAGASIN CITA NB'}</td>
                <td>${weighing.fournisseur_nom || 'MAGASIN NCB CITA'}</td>
                  <td class="center">30</td>
                  <td class="center">${poidsNetParsed.toLocaleString()}</td>
                <td>${weighing.produit_nom || 'NOIX DE CAJOU'}</td>
                <td>${weighing.numero_lot || ''}</td>
              </tr>
              `;
            }).join('')}
            <tr class="total-row">
              <td colspan="6"><strong>TOTAL:</strong></td>
              <td class="center"><strong>${totalNSA}</strong></td>
              <td class="center"><strong>${totalPNAcc.toLocaleString()}</strong></td>
              <td colspan="2"></td>
            </tr>
          </tbody>
        </table>
        
        <div class="signature-section">
          <div class="signature-box">
            <div class="signature-line"></div>
            <div>Signature 1</div>
          </div>
          <div class="signature-box">
            <div class="signature-line"></div>
            <div>Signature 2</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ 
      ...prev, 
      [field]: value,
      page: 1 // Reset à la première page lors du filtrage
    }));
  };

  const handleAdvancedSearch = (newFilters: HistoryFilters) => {
    setFilters({
      ...newFilters,
      page: 1,
      page_size: newFilters.page_size || filters.page_size || 20,
    });
    setCurrentPage(1);
    // loadWeighings se déclenche via useEffect([filters])
  };

  const getListAdvancedFiltersCount = () => {
    const keys: (keyof HistoryFilters)[] = [
      'type_pesee', 'date_from', 'date_to', 'vehicule', 'type_vehicule',
      'remorque', 'chauffeur', 'convoyeur', 'client', 'fournisseur',
      'transporteur', 'exportateur', 'magasin', 'conditionnement', 'provenance',
    ];
    return keys.filter((key) => Boolean(filters[key])).length;
  };

  const clearAllListFilters = () => {
    setFilters({
      search: '',
      produit: '',
      statut: '',
      type_pesee: '',
      date_from: '',
      date_to: '',
      vehicule: '',
      type_vehicule: '',
      remorque: '',
      chauffeur: '',
      convoyeur: '',
      client: '',
      fournisseur: '',
      transporteur: '',
      exportateur: '',
      magasin: '',
      conditionnement: '',
      provenance: '',
      page: 1,
      page_size: filters.page_size || 20,
    });
    setCurrentPage(1);
  };

  const handleExportClick = (type: 'pdf' | 'excel' | 'csv') => {
    setExportType(type);
    setShowExportModal(true);
  };

  const handlePageChange = (page: number) => {
    // Vérifier que la page est valide
    if (page < 1 || page > totalPages) {
      return;
    }
    
    setFilters(prev => ({ ...prev, page }));
    setCurrentPage(page);
    
    // Scroll vers le haut de la table pour une meilleure UX
    const tableElement = document.querySelector('.overflow-x-auto');
    if (tableElement) {
      tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedPesee(null);
    setMenuPosition(null);
  };

  const handleActionMenu = (pesee: HistoryPesee, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Capturer les coordonnées avant tout traitement
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const menuPosition = { 
      x: Math.max(10, rect.left - 200), // Positionner à gauche avec une marge
      y: rect.bottom + 5 // Juste en dessous du bouton
    };
    
    // Fermer le modal de détail s'il est ouvert
    if (showDetailModal) {
      setShowDetailModal(false);
      // Petit délai pour s'assurer que le modal se ferme complètement
      setTimeout(() => {
        setSelectedPesee(pesee);
        setMenuPosition(menuPosition);
      }, 100);
    } else {
      setSelectedPesee(pesee);
      setMenuPosition(menuPosition);
    }
  };

  const handleAction = async (action: string, pesee: HistoryPesee) => {
    try {
      switch (action) {
        case 'voir_detail':
          // Ouvrir le modal de détail
          setSelectedPesee(pesee); // S'assurer que selectedPesee est défini
          setShowDetailModal(true);
          // Fermer le menu après avoir ouvert le modal
          setMenuPosition(null);
          break;
        
        case 'continuer':
          // Fermer le menu avant la redirection
          setSelectedPesee(null);
          setMenuPosition(null);
          // Rediriger vers la page de pesée avec l'ID
          window.location.href = `/nouvelle-pesee?weighingId=${pesee.id}`;
          break;
        
        case 'supprimer': {
          const peseeId = String(pesee.id);
          if (!peseeId || peseeId === 'undefined' || peseeId === 'null') {
            throw new Error('Identifiant de pesée invalide');
          }
          await historyService.deletePesee(peseeId);
          setWeighings(prev => prev.filter(w => String(w.id) !== peseeId));
          setSelectedPesee(null);
          setMenuPosition(null);
          await loadWeighings();
          break;
        }
        
        case 'ajouter_stock':
          // Fermer le menu avant la confirmation
          setSelectedPesee(null);
          setMenuPosition(null);
          if (window.confirm(`Êtes-vous sûr de vouloir ajouter la pesée ${pesee.id} au stock ?`)) {
            await historyService.addToStock(pesee.id);
            loadWeighings(); // Recharger la liste
            alert('Pesée ajoutée au stock avec succès');
          }
          break;
        
        case 'envoyer_email':
          // Fermer le menu avant d'ouvrir le modal email
          setMenuPosition(null);
          setSelectedPesee(pesee); // Garder la pesée sélectionnée pour le modal
          const ticketData = historyService.convertPeseeToWeighingTicketData(pesee);
          setEmailData({
            recipients: '',
            subject: `Ticket de Pesée - ${ticketData.code}`,
            message: `Bonjour,\n\nVeuillez trouver ci-joint le ticket de pesée ${ticketData.code} pour la pesée effectuée le ${ticketData.weighingDate}.\n\nCordialement,\nService de Pesée`,
            cc: '',
            bcc: ''
          });
          setShowEmailModal(true);
          break;
        
        case 'refuser':
          // Fermer le menu
          setSelectedPesee(null);
          setMenuPosition(null);
          // Ouvrir le modal de refoulement
          setShowRefoulementModal(true);
          setSelectedPeseeForRefoulement(pesee);
          break;
        
        case 'refouler':
          // Recharger la liste après refoulement
          loadWeighings();
          break;

        case 'modifier':
          setSelectedPesee(null);
          setMenuPosition(null);
          loadWeighings();
          break;
        
        case 'voir_acteurs':
          // Fermer le menu
          setSelectedPesee(null);
          setMenuPosition(null);
          // Cette action est gérée dans le menu avec un modal
          console.log('Voir les acteurs de la pesée:', pesee.id);
          break;
        
        case 'imprimer_ticket':
          // Fermer le menu avant l'impression
          setSelectedPesee(null);
          setMenuPosition(null);
          // Imprimer le ticket
          try {
            // Vérifier si la pesée est refoulée
            const isRefoulee = pesee.statut === 'REFOULEE';
            
            if (isRefoulee) {
              // Récupérer les données de refoulement
              const peseeIdStr = String(pesee.id);
              const refoulementData = await historyService.getRefoulementData(peseeIdStr);

              if (refoulementData) {
                // Récupérer les données complètes de la pesée
                let peseeComplete: HistoryPesee | Pesée = pesee;
                try {
                  const peseeIdNum =
                    typeof pesee.id === 'string' ? parseInt(pesee.id, 10) : Number(pesee.id);
                  const peseeResponse = await weighingService.getPesée(peseeIdNum);
                  if (peseeResponse?.data) {
                    peseeComplete = peseeResponse.data as Pesée;
                  }
                } catch (error) {
                  console.warn('Impossible de récupérer les données complètes de la pesée, utilisation des données disponibles');
                }
                
                // Convertir en RefoulementTicketData
                const refoulementTicketData = convertToRefoulementTicketData(refoulementData, peseeComplete);
                
                // Imprimer le ticket de refoulement
                await refoulementTicketService.printTicket(refoulementTicketData, {
                  onError: (err: string) => {
                    console.error('Erreur lors de l\'impression du ticket de refoulement:', err);
                    alert('Erreur lors de l\'impression du ticket de refoulement');
                  }
                });
              } else {
                alert('Aucune donnée de refoulement trouvée pour cette pesée');
              }
            } else {
              // Imprimer le ticket normal
              const ticketData = historyService.convertPeseeToWeighingTicketData(pesee);
              // Utiliser le service d'impression normal
              const { default: ticketActionsService } = await import('@/services/ticketActionsService');
              await ticketActionsService.printTicket(ticketData, {
                onSuccess: () => {
                  console.log('Ticket imprimé avec succès');
                },
                onError: (error) => {
                  console.error('Erreur lors de l\'impression:', error);
                  alert('Erreur lors de l\'impression du ticket');
                }
              });
            }
          } catch (error: any) {
            console.error('Erreur lors de l\'impression:', error);
            alert(error.message || 'Erreur lors de l\'impression du ticket');
          }
          break;
        
        case 'exporter_pdf':
          // Fermer le menu avant l'export
          setSelectedPesee(null);
          setMenuPosition(null);
          // Exporter le ticket en PDF
          try {
            const ticketData = historyService.convertPeseeToWeighingTicketData(pesee);
            // TODO: Implémenter l'export PDF du ticket
            console.log('Export PDF du ticket:', ticketData);
            alert('Export PDF du ticket lancé');
          } catch (error) {
            console.error('Erreur lors de l\'export PDF:', error);
            alert('Erreur lors de l\'export PDF du ticket');
          }
          break;
        
        default:
          // Fermer le menu pour les actions non implémentées
          setSelectedPesee(null);
          setMenuPosition(null);
          console.log('Action non implémentée:', action);
      }
    } catch (error: any) {
      console.error('Erreur lors de l\'action:', error);
      const message = error.message || 'Erreur lors de l\'exécution de l\'action';
      setError(message);
      alert(message);
      if (action !== 'supprimer') {
        setSelectedPesee(null);
        setMenuPosition(null);
      }
      throw error;
    }
  };

  const getStatusInfo = (weighing: HistoryPesee) => {
    const statut = weighing.statut;
    const statusMap: { [key: string]: { label: string; color: string; icon: string } } = {
      'EN_COURS': { label: 'En cours', color: 'bg-yellow-100 text-yellow-800', icon: 'fas fa-play' },
      'EN_ATTENTE_TARE': { label: 'En attente tare', color: 'bg-orange-100 text-orange-800', icon: 'fas fa-clock' },
      'TARE_ENREGISTREE': { label: 'Tare enregistrée', color: 'bg-blue-100 text-blue-800', icon: 'fas fa-weight' },
      'BRUT_ENREGISTRE': { label: 'Brut enregistré', color: 'bg-indigo-100 text-indigo-800', icon: 'fas fa-weight-hanging' },
      'COMPLETEE': { label: 'Complétée', color: 'bg-green-100 text-green-800', icon: 'fas fa-check' },
      'ACHEVEE': { label: 'Achevée', color: 'bg-green-100 text-green-800', icon: 'fas fa-check-circle' },
      'REFOULEE': { label: 'Refoulée', color: 'bg-red-100 text-red-800', icon: 'fas fa-times' },
      'ANNULEE': { label: 'Annulée', color: 'bg-gray-100 text-gray-800', icon: 'fas fa-ban' },
    };
    return statusMap[statut] || { label: statut, color: 'bg-gray-100 text-gray-800', icon: 'fas fa-question' };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex items-center justify-between mb-6 p-6">
        <div className="flex items-center">
          <button 
            onClick={() => window.history.back()}
            className="p-2 text-gray-400 hover:text-gray-600 mr-4"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1 className="text-xl font-medium text-gray-900">Historique des Pesées</h1>
        </div>
        <div className="text-sm text-gray-600">
          {activeTab === 'list' && `${totalCount} pesée${totalCount > 1 ? 's' : ''} trouvée${totalCount > 1 ? 's' : ''}`}
          {activeTab === 'bordereau' && `${bordereauWeighings.length} pesée${bordereauWeighings.length > 1 ? 's' : ''} pour le bordereau`}
        </div>
      </div>

      {/* Onglets */}
      <div className="px-6 mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('list')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'list'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-list mr-2"></i>
              Liste des Pesées
            </button>
            <button
              onClick={() => setActiveTab('bordereau')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'bordereau'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="fas fa-file-alt mr-2"></i>
              Bordereau
            </button>
          </nav>
        </div>
      </div>
      
      <div className="px-6">
        {/* Contenu de l'onglet Liste */}
        {activeTab === 'list' && (
          <div className="bg-white rounded-xl shadow-sm">
            {/* Header with Filters */}
            <div className="p-6 border-b border-gray-200 bg-gray-100 rounded-t-xl">
            <div className="flex justify-between items-center">
              <h3 className="font-medium text-gray-900">Liste des Pesées</h3>
              <div className="flex space-x-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                  <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
                <SearchableSelect
                  value={filters.produit || ''}
                  onChange={(value) => handleFilterChange('produit', value)}
                  options={productOptions}
                  placeholder="Tous produits"
                  className="w-48"
                />
                <select
                  value={filters.statut}
                  onChange={(e) => handleFilterChange('statut', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                >
                  <option value="">Tous statuts</option>
                  <option value="EN_COURS">En cours</option>
                  <option value="EN_ATTENTE_TARE">En attente tare</option>
                  <option value="TARE_ENREGISTREE">Tare enregistrée</option>
                  <option value="BRUT_ENREGISTRE">Brut enregistré</option>
                  <option value="COMPLETEE">Complétée</option>
                  <option value="ACHEVEE">Achevée</option>
                  <option value="REFOULEE">Refoulée</option>
                </select>
                <div className="flex space-x-2">
                  <PermissionButton
                    permission={PERMISSIONS.AUDIT_REPORTS}
                    onClick={() => handleExportClick('pdf')}
                    disabled={weighings.length === 0}
                    className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm transition-colors flex items-center"
                  >
                    <i className="fas fa-file-pdf mr-1"></i>
                    PDF
                  </PermissionButton>
                  <PermissionButton
                    permission={PERMISSIONS.AUDIT_REPORTS}
                    onClick={() => handleExportClick('excel')}
                    disabled={weighings.length === 0}
                    className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm transition-colors flex items-center"
                  >
                    <i className="fas fa-file-excel mr-1"></i>
                    EXCEL
                  </PermissionButton>
                  <PermissionButton
                    permission={PERMISSIONS.AUDIT_REPORTS}
                    onClick={() => handleExportClick('csv')}
                    disabled={weighings.length === 0}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm transition-colors flex items-center"
                  >
                    <i className="fas fa-file-csv mr-1"></i>
                    CSV
                  </PermissionButton>
                  <PermissionButton
                    permission={PERMISSIONS.WEIGHING_VIEW}
                    onClick={() => setShowAdvancedSearch(true)}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors flex items-center"
                  >
                    <i className="fas fa-sliders-h mr-2"></i>
                    Plus de filtres
                    {getListAdvancedFiltersCount() > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 bg-white text-purple-700 rounded-full text-xs font-semibold">
                        {getListAdvancedFiltersCount()}
                      </span>
                    )}
                  </PermissionButton>
                </div>
              </div>
            </div>
            
            {/* Indicateur de filtres actifs */}
            {(filters.search || filters.produit || filters.statut || getListAdvancedFiltersCount() > 0) && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center text-sm text-gray-600 flex-wrap gap-2">
                    <span className="inline-flex items-center mr-1">
                      <i className="fas fa-filter text-blue-500 mr-2"></i>
                      Filtres actifs:
                    </span>
                    {filters.search && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                        Recherche: {filters.search}
                      </span>
                    )}
                    {filters.produit && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                        Produit
                      </span>
                    )}
                    {filters.statut && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                        Statut: {filters.statut}
                      </span>
                    )}
                    {filters.type_pesee && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                        Type pesée
                      </span>
                    )}
                    {filters.date_from && filters.date_to && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                        Période: {filters.date_from} → {filters.date_to}
                      </span>
                    )}
                    {filters.type_vehicule && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Type véhicule</span>
                    )}
                    {filters.vehicule && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Véhicule</span>
                    )}
                    {filters.remorque && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Remorque</span>
                    )}
                    {filters.chauffeur && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Chauffeur</span>
                    )}
                    {filters.convoyeur && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Convoyeur</span>
                    )}
                    {filters.client && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Client</span>
                    )}
                    {filters.fournisseur && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Fournisseur</span>
                    )}
                    {filters.transporteur && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Transporteur</span>
                    )}
                    {filters.exportateur && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Exportateur</span>
                    )}
                    {filters.magasin && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Magasin</span>
                    )}
                    {filters.conditionnement && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Conditionnement</span>
                    )}
                    {filters.provenance && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                        Provenance: {filters.provenance}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={clearAllListFilters}
                    className="text-xs text-red-600 hover:text-red-800 whitespace-nowrap"
                  >
                    <i className="fas fa-times mr-1"></i>Effacer tous les filtres
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <div className="flex">
                <div className="flex-shrink-0">
                  <i className="fas fa-exclamation-circle text-red-400"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <LoadingSpinner />
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Camion</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Poids Net (kg)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chauffeur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {weighings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                        <i className="fas fa-inbox text-4xl text-gray-300 mb-4"></i>
                        <p>Aucune pesée trouvée</p>
                      </td>
                    </tr>
                  ) : (
                    weighings.map((weighing) => {
                      const statusInfo = getStatusInfo(weighing);
                      return (
                        <tr key={weighing.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm">
                            {formatDate(weighing.date_heure)}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-blue-600">
                            {historyService.getPeseeReference(weighing)}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {weighing.camion_immatriculation || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {weighing.produit_nom || 'Non spécifié'}
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold">
                            {weighing.poids_net ? `${weighing.poids_net.toLocaleString()} kg` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {weighing.chauffeur_nom || 'Non spécifié'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded text-xs flex items-center w-fit ${statusInfo.color}`}>
                              <i className={`${statusInfo.icon} mr-1`}></i>
                              {statusInfo.label}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <PermissionButton
                              permission={PERMISSIONS.WEIGHING_VIEW}
                              onClick={(e) => handleActionMenu(weighing, e)}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Actions"
                            >
                              <i className="fas fa-ellipsis-v"></i>
                            </PermissionButton>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination améliorée */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                {/* Informations de pagination */}
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    Affichage de {((currentPage - 1) * (filters.page_size || 20)) + 1} à {Math.min(currentPage * (filters.page_size || 20), totalCount)} sur {totalCount} pesée{totalCount > 1 ? 's' : ''}
                  </span>
                  
                  {/* Sélecteur de taille de page */}
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Afficher:</span>
                    <select
                      value={filters.page_size || 20}
                      onChange={(e) => {
                        const newPageSize = parseInt(e.target.value);
                        setFilters(prev => ({ 
                          ...prev, 
                          page_size: newPageSize,
                          page: 1 
                        }));
                        setCurrentPage(1);
                      }}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-orange-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-600">par page</span>
                  </div>
                </div>

                {/* Navigation de pagination */}
                <div className="flex items-center space-x-2">
                  {/* Bouton Première page */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage <= 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Première page"
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>

                  {/* Bouton Page précédente */}
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Page précédente"
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>

                  {/* Numéros de pages */}
                  <div className="flex items-center space-x-1">
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                      
                      // Ajuster si on est proche de la fin
                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }

                      // Page 1 avec ellipsis si nécessaire
                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => handlePageChange(1)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span key="ellipsis1" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                      }

                      // Pages visibles
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            className={`px-3 py-2 border rounded-lg text-sm transition-colors ${
                              i === currentPage
                                ? 'bg-orange-500 text-white border-orange-500'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      // Ellipsis et dernière page si nécessaire
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span key="ellipsis2" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => handlePageChange(totalPages)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                          >
                            {totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  {/* Bouton Page suivante */}
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Page suivante"
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>

                  {/* Bouton Dernière page */}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Dernière page"
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>

                  {/* Navigation rapide */}
                  <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-300">
                    <span className="text-sm text-gray-600">Aller à:</span>
                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= totalPages) {
                          handlePageChange(page);
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const page = parseInt((e.target as HTMLInputElement).value);
                          if (page >= 1 && page <= totalPages) {
                            handlePageChange(page);
                          }
                        }
                      }}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:ring-2 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-600">sur {totalPages}</span>
                  </div>
                </div>
              </div>

              {/* Indicateur de progression */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${(currentPage / totalPages) * 100}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contenu de l'onglet Bordereau */}
      {activeTab === 'bordereau' && (
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header avec filtres pour le bordereau */}
          <div className="p-6 border-b border-gray-200 bg-gray-100 rounded-t-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-gray-900">Génération de Bordereau</h3>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowBordereauAdvancedSearch(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors flex items-center"
                >
                  <i className="fas fa-sliders-h mr-2"></i>
                  Plus de filtres
                  {getBordereauAdvancedFiltersCount() > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-white text-purple-700 rounded-full text-xs font-semibold">
                      {getBordereauAdvancedFiltersCount()}
                    </span>
                  )}
                </button>
                <button
                  onClick={loadBordereauWeighings}
                  disabled={bordereauLoading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm transition-colors flex items-center"
                >
                  {bordereauLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <i className="fas fa-search mr-2"></i>
                  )}
                  Charger les Pesées
                </button>
                <button
                  onClick={generateBordereau}
                  disabled={bordereauWeighings.length === 0}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm transition-colors flex items-center"
                >
                  <i className="fas fa-eye mr-2"></i>
                  Aperçu
                </button>
                <button
                  onClick={printBordereau}
                  disabled={bordereauWeighings.length === 0}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded-lg text-sm transition-colors flex items-center"
                >
                  <i className="fas fa-print mr-2"></i>
                  Imprimer
                </button>
              </div>
            </div>

            {/* Filtres pour le bordereau */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <input
                  type="date"
                  value={bordereauFilters.dateFrom}
                  onChange={(e) => handleBordereauFilterChange('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                <input
                  type="date"
                  value={bordereauFilters.dateTo}
                  onChange={(e) => handleBordereauFilterChange('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Produit</label>
                <SearchableSelect
                  value={bordereauFilters.produit}
                  onChange={(value) => handleBordereauFilterChange('produit', value)}
                  options={productOptions}
                  placeholder="Tous produits"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
                <input
                  type="text"
                  value={bordereauFilters.fournisseur}
                  onChange={(e) => handleBordereauFilterChange('fournisseur', e.target.value)}
                  placeholder="Nom du fournisseur"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provenance</label>
                <input
                  type="text"
                  value={bordereauFilters.provenance}
                  onChange={(e) => handleBordereauFilterChange('provenance', e.target.value)}
                  placeholder="Lieu de provenance"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de pesée</label>
                <select
                  value={bordereauFilters.type_pesee}
                  onChange={(e) => handleBordereauFilterChange('type_pesee', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                >
                  <option value="">Tous types</option>
                  <option value="reception">Réception</option>
                  <option value="transfert">Transfert</option>
                  <option value="expedition">Expédition</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={bordereauFilters.statut}
                  onChange={(e) => handleBordereauFilterChange('statut', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                >
                  <option value="">Tous les statuts</option>
                  <option value="COMPLETEE">Complétée</option>
                  <option value="ACHEVEE">Achevée</option>
                  <option value="TARE_ENREGISTREE">Tare enregistrée</option>
                  <option value="BRUT_ENREGISTRE">Brut enregistré</option>
                  <option value="EN_COURS">En cours</option>
                  <option value="EN_ATTENTE_TARE">En attente tare</option>
                  <option value="REFOULEE">Refoulée</option>
                </select>
              </div>
            </div>

            {getBordereauAdvancedFiltersCount() > 0 && (
              <div className="mt-3 flex items-center justify-between">
                <div className="flex flex-wrap gap-2 text-xs">
                  {bordereauFilters.type_vehicule && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Type véhicule</span>
                  )}
                  {bordereauFilters.vehicule && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Véhicule</span>
                  )}
                  {bordereauFilters.remorque && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Remorque</span>
                  )}
                  {bordereauFilters.chauffeur && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Chauffeur</span>
                  )}
                  {bordereauFilters.client && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Client</span>
                  )}
                  {bordereauFilters.magasin && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Magasin</span>
                  )}
                  {bordereauFilters.conditionnement && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Conditionnement</span>
                  )}
                  {bordereauFilters.transporteur && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Transporteur</span>
                  )}
                  {bordereauFilters.exportateur && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Exportateur</span>
                  )}
                  {bordereauFilters.convoyeur && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">Convoyeur</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={clearBordereauAdvancedFilters}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  <i className="fas fa-times mr-1"></i>Effacer filtres avancés
                </button>
              </div>
            )}

            {/* Cartes récapitulatives */}
            {bordereauWeighings.length > 0 && (() => {
              const totalCount = bordereauWeighings.length;
              const totalNet = bordereauWeighings.reduce((sum, w) => {
                const net = typeof w.poids_net === 'string' ? parseFloat(w.poids_net) : Number(w.poids_net || 0);
                return sum + (Number.isFinite(net) ? net : 0);
              }, 0);
              const completedCount = bordereauWeighings.filter((w) =>
                ['COMPLETEE', 'ACHEVEE'].includes(w.statut)
              ).length;
              const rejectedCount = bordereauWeighings.filter((w) => w.statut === 'REFOULEE').length;
              const inProgressCount = bordereauWeighings.filter((w) =>
                ['EN_COURS', 'EN_ATTENTE_TARE', 'BRUT_ENREGISTRE', 'TARE_ENREGISTREE'].includes(w.statut)
              ).length;

              const cards = [
                {
                  key: 'total',
                  label: 'Éléments trouvés',
                  value: totalCount.toLocaleString('fr-FR'),
                  icon: 'fas fa-list',
                  color: 'bg-blue-50 border-blue-200 text-blue-700',
                  iconBg: 'bg-blue-100 text-blue-600',
                },
                {
                  key: 'net',
                  label: 'Poids net total',
                  value: `${totalNet.toLocaleString('fr-FR')} kg`,
                  icon: 'fas fa-weight-hanging',
                  color: 'bg-indigo-50 border-indigo-200 text-indigo-700',
                  iconBg: 'bg-indigo-100 text-indigo-600',
                },
                {
                  key: 'completed',
                  label: 'Complétées',
                  value: completedCount.toLocaleString('fr-FR'),
                  icon: 'fas fa-check-circle',
                  color: 'bg-green-50 border-green-200 text-green-700',
                  iconBg: 'bg-green-100 text-green-600',
                },
                {
                  key: 'rejected',
                  label: 'Refoulées',
                  value: rejectedCount.toLocaleString('fr-FR'),
                  icon: 'fas fa-ban',
                  color: 'bg-red-50 border-red-200 text-red-700',
                  iconBg: 'bg-red-100 text-red-600',
                },
                {
                  key: 'progress',
                  label: 'En cours',
                  value: inProgressCount.toLocaleString('fr-FR'),
                  icon: 'fas fa-spinner',
                  color: 'bg-amber-50 border-amber-200 text-amber-700',
                  iconBg: 'bg-amber-100 text-amber-600',
                },
              ];

              return (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {cards.map((card) => (
                    <div
                      key={card.key}
                      className={`rounded-xl border p-4 ${card.color}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-medium opacity-80">{card.label}</p>
                          <p className="mt-1 text-xl font-bold tracking-tight">{card.value}</p>
                        </div>
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${card.iconBg}`}>
                          <i className={`${card.icon} text-sm`}></i>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Liste des pesées pour le bordereau */}
          <div className="overflow-x-auto">
            {bordereauLoading ? (
              <div className="flex items-center justify-center p-8">
                <LoadingSpinner />
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Camion</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Poids Net (kg)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provenance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bordereauWeighings.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                        <i className="fas fa-inbox text-4xl text-gray-300 mb-4"></i>
                        <p>Aucune pesée chargée pour le bordereau</p>
                        <p className="text-sm mt-2">Utilisez les filtres ci-dessus et cliquez sur "Charger les Pesées"</p>
                      </td>
                    </tr>
                  ) : (
                    bordereauWeighings.map((weighing) => (
                      <tr key={weighing.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-blue-600">
                          {weighing.reference || weighing.id}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {formatDate(weighing.date_heure)}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {weighing.camion_immatriculation || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {weighing.produit_nom || 'Non spécifié'}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold">
                          {weighing.poids_net ? `${weighing.poids_net.toLocaleString()} kg` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {weighing.fournisseur_nom || 'Non spécifié'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {weighing.provenance || 'Non spécifié'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      </div>

      {/* Menu d'actions contextuel */}
      {selectedPesee && menuPosition && (
        <HistoryActionsMenu
          pesee={selectedPesee}
          onAction={handleAction}
          onClose={() => {
            setSelectedPesee(null);
            setMenuPosition(null);
          }}
          position={menuPosition}
        />
      )}

      {/* Modal d'envoi d'email */}
      {showEmailModal && selectedPesee && (
        <EmailModal
          ticketData={historyService.convertPeseeToWeighingTicketData(selectedPesee)}
          emailData={emailData}
          onEmailDataChange={setEmailData}
          onSend={() => setShowEmailModal(false)}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* Modal de recherche avancée (liste) */}
      <AdvancedSearchModal
        isOpen={showAdvancedSearch}
        onClose={() => setShowAdvancedSearch(false)}
        onApplyFilters={handleAdvancedSearch}
        currentFilters={filters}
        isLoading={loading}
        resultsCount={weighings.length}
      />

      {/* Modal de filtres avancés (bordereau) */}
      <AdvancedSearchModal
        isOpen={showBordereauAdvancedSearch}
        onClose={() => setShowBordereauAdvancedSearch(false)}
        onApplyFilters={handleBordereauAdvancedSearch}
        currentFilters={{
          produit: bordereauFilters.produit,
          statut: bordereauFilters.statut,
          type_pesee: bordereauFilters.type_pesee,
          date_from: bordereauFilters.dateFrom,
          date_to: bordereauFilters.dateTo,
          fournisseur: bordereauFilters.fournisseur,
          provenance: bordereauFilters.provenance,
          vehicule: bordereauFilters.vehicule,
          type_vehicule: bordereauFilters.type_vehicule,
          remorque: bordereauFilters.remorque,
          chauffeur: bordereauFilters.chauffeur,
          convoyeur: bordereauFilters.convoyeur,
          client: bordereauFilters.client,
          transporteur: bordereauFilters.transporteur,
          exportateur: bordereauFilters.exportateur,
          magasin: bordereauFilters.magasin,
          conditionnement: bordereauFilters.conditionnement,
          page: 1,
          page_size: 1000,
        }}
        isLoading={bordereauLoading}
        resultsCount={bordereauWeighings.length}
      />

      {/* Modal d'export avec sélection de champs */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        weighings={weighings}
        exportType={exportType}
      />

      {/* Modal de détail de la pesée */}
      <PeseeDetailsModal
        isOpen={showDetailModal && selectedPesee !== null && !menuPosition}
        pesee={selectedPesee}
        onClose={handleCloseDetailModal}
        title={selectedPesee ? `Détail de la pesée ${historyService.getPeseeReference(selectedPesee)}` : undefined}
      />

      {/* Modal d'aperçu du bordereau */}
      {showBordereauPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Aperçu du Bordereau
              </h3>
              <div className="flex space-x-3">
                <button
                  onClick={printBordereau}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  <i className="fas fa-print mr-2"></i>
                  Imprimer
                </button>
                <button
                  onClick={() => setShowBordereauPreview(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Fermer
                </button>
              </div>
            </div>
            
            <div 
              className="border border-gray-300 p-4 bg-white"
              dangerouslySetInnerHTML={{ __html: generateBordereauHTML() }}
            />
          </div>
        </div>
      )}

      {/* Modal de refoulement */}
      {showRefoulementModal && selectedPeseeForRefoulement && (
        <RefoulementModal
          isOpen={showRefoulementModal}
          onClose={() => {
            setShowRefoulementModal(false);
            setSelectedPeseeForRefoulement(null);
          }}
          onSuccess={() => {
            // Recharger la liste des pesées après refoulement
            loadWeighings();
            setShowRefoulementModal(false);
            setSelectedPeseeForRefoulement(null);
            alert(`Pesée ${selectedPeseeForRefoulement.reference || selectedPeseeForRefoulement.id} refoulée avec succès`);
          }}
          pesee={selectedPeseeForRefoulement}
          peseeId={typeof selectedPeseeForRefoulement.id === 'string' ? parseInt(selectedPeseeForRefoulement.id) : selectedPeseeForRefoulement.id}
          peseeReference={selectedPeseeForRefoulement.reference || selectedPeseeForRefoulement.référence}
          autoLoadPesee={false}
          showTicketAfterCreation={true}
        />
      )}
    </div>
  );
};

export default HistoryPage;
